import { useState } from 'react';
import { useStore } from '../store.js';

const STATES = ['AL','AK','AZ','AR','CA','CO','CT','DE','FL','GA','HI','ID','IL','IN','IA','KS','KY','LA','ME','MD','MA','MI','MN','MS','MO','MT','NE','NV','NH','NJ','NM','NY','NC','ND','OH','OK','OR','PA','RI','SC','SD','TN','TX','UT','VT','VA','WA','WV','WI','WY','DC','PR'];

const empty = {
  first_name: '', last_name: '', ownership_pct: '',
  email: '', phone: '',
  ssn: '', dob: '',
  address_line1: '', address_line2: '', city: '', state: '', zip: '',
};

function validateDraft(draft) {
  const errors = {};
  if (!draft.first_name?.trim()) errors.first_name = 'Required.';
  if (!draft.last_name?.trim())  errors.last_name  = 'Required.';

  const pct = Number(draft.ownership_pct);
  if (!pct || pct <= 0 || pct > 100) errors.ownership_pct = 'Must be 1–100.';

  const ssnDigits = (draft.ssn || '').replace(/\D/g, '');
  if (ssnDigits.length !== 9) errors.ssn = 'Must be 9 digits.';

  if (!draft.dob) {
    errors.dob = 'Required.';
  } else {
    const dob = new Date(draft.dob);
    if (isNaN(dob.getTime())) errors.dob = 'Invalid date.';
    else {
      const age = (Date.now() - dob.getTime()) / (365.25 * 24 * 3600 * 1000);
      if (age < 18) errors.dob = 'Owner must be at least 18.';
      else if (age > 110) errors.dob = 'Date looks incorrect.';
    }
  }

  if (!draft.address_line1?.trim()) errors.address_line1 = 'Required.';
  if (!draft.city?.trim())          errors.city          = 'Required.';
  if (!draft.state?.trim())         errors.state         = 'Required.';
  const zip = (draft.zip || '').replace(/\D/g, '').slice(0, 5);
  if (!/^\d{5}$/.test(zip))         errors.zip           = 'Must be 5 digits.';

  return errors;
}

// Additional owners are INVITED: the first signer only supplies name + email.
// The co-owner gets their own secure link to complete their section and sign.
function validateInvite(draft) {
  const errors = {};
  if (!draft.first_name?.trim()) errors.first_name = 'Required.';
  if (!draft.last_name?.trim())  errors.last_name  = 'Required.';
  if (!draft.email?.trim() || !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(draft.email.trim())) {
    errors.email = 'A valid email is required — we send them their own secure link.';
  }
  return errors;
}

function isDraftFilled(draft) {
  return !!(draft.first_name?.trim() || draft.last_name?.trim() || draft.ownership_pct || draft.email?.trim());
}

// An owner whose section still has to be completed by themselves (invited co-owner).
function isInvited(o) {
  return o.owner_index > 0 && !o.ssn_last4;
}

export function Owners({ onNext, onBack }) {
  const owners = useStore((s) => s.owners);
  const addOwner = useStore((s) => s.addOwner);
  const removeOwner = useStore((s) => s.removeOwner);
  const updateOwner = useStore((s) => s.updateOwner);
  const [draft, setDraft] = useState(empty);
  const [editingId, setEditingId] = useState(null);
  const [errors, setErrors] = useState({});
  const [globalError, setGlobalError] = useState(null);
  const [busy, setBusy] = useState(false);

  const totalPct = owners.reduce((a, o) => a + Number(o.ownership_pct || 0), 0);
  const draftFilled = isDraftFilled(draft);

  const editingOwner = editingId ? owners.find((o) => o.id === editingId) : null;
  // Invite mode: adding any owner beyond the first, or editing an invited one.
  const inviteMode = editingId ? isInvited(editingOwner || {}) : owners.length >= 1;

  function patch(k, v) {
    setDraft((d) => ({ ...d, [k]: v }));
    if (errors[k]) setErrors((e) => { const { [k]: _, ...rest } = e; return rest; });
  }

  async function commitDraft() {
    setGlobalError(null);
    const errs = inviteMode ? validateInvite(draft) : validateDraft(draft);
    if (Object.keys(errs).length > 0) {
      setErrors(errs);
      return false;
    }
    setErrors({});
    const body = inviteMode
      ? {
          first_name: draft.first_name.trim(),
          last_name: draft.last_name.trim(),
          email: draft.email.trim(),
          phone: draft.phone?.trim() || null,
        }
      : { ...draft, ownership_pct: Number(draft.ownership_pct) };
    setBusy(true);
    try {
      if (editingId) await updateOwner(editingId, body);
      else await addOwner(body);
      setDraft(empty);
      setEditingId(null);
      return true;
    } catch (e) {
      setGlobalError(e.body?.message || e.message);
      return false;
    } finally {
      setBusy(false);
    }
  }

  async function handleContinue() {
    setGlobalError(null);
    if (draftFilled) {
      const ok = await commitDraft();
      if (!ok) return;
    }
    const fresh = useStore.getState().owners;
    if (fresh.length === 0) {
      setGlobalError('Please add at least one owner before continuing.');
      return;
    }
    // A prefilled owner never carries an SSN over — it must be re-entered each time.
    const primary = fresh.find((o) => o.owner_index === 0) || fresh[0];
    if (primary && !primary.ssn_last4) {
      setGlobalError(`Please click Edit next to ${primary.first_name} ${primary.last_name} and enter their SSN and date of birth before continuing.`);
      return;
    }
    onNext();
  }

  function startEdit(o) {
    setDraft({ ...empty, ...o, ownership_pct: o.ownership_pct ?? '' });
    setEditingId(o.id);
    setErrors({});
    setGlobalError(null);
  }

  return (
    <div className="space-y-6">
      <header>
        <h2 className="text-xl font-semibold text-brand-900">Owners</h2>
        <p className="text-sm text-slate-500">
          Add every individual who owns 20%+ of the business. For co-owners, you only enter their
          name and email — after you sign, they receive their own secure link to fill in their
          personal information and sign their section themselves.
        </p>
      </header>

      {owners.length > 0 && (
        <div className="card divide-y divide-slate-200">
          {owners.map((o) => (
            <div key={o.id} className="px-4 py-3 flex items-center justify-between">
              <div>
                <p className="font-medium">{o.first_name} {o.last_name}</p>
                {isInvited(o) ? (
                  <p className="text-xs text-slate-500">
                    <span className="text-brand-700 font-medium">Co-owner invite</span> · {o.email || 'no email'} — completes their own section &amp; signs after you
                  </p>
                ) : (
                  <p className="text-xs text-slate-500">{o.ownership_pct}% · {o.email || 'no email'} · {o.phone || 'no phone'}</p>
                )}
              </div>
              <div className="flex gap-2">
                <button className="text-brand-700 text-sm hover:underline" onClick={() => startEdit(o)}>Edit</button>
                <button className="text-red-600 text-sm hover:underline" onClick={() => removeOwner(o.id)}>Remove</button>
              </div>
            </div>
          ))}
          <div className="px-4 py-2 text-xs text-slate-500 bg-slate-50">
            Total ownership: <strong>{totalPct}%</strong>
            {totalPct > 100 && <span className="text-red-600 ml-2">(over 100% — please adjust)</span>}
          </div>
        </div>
      )}

      <div className="card p-4 space-y-4">
        <h3 className="font-medium text-slate-800">
          {editingId ? (inviteMode ? 'Edit co-owner invite' : 'Edit owner') : owners.length === 0 ? 'Add the primary owner' : 'Invite another owner'}
        </h3>
        {inviteMode ? (
          <>
            <p className="text-xs text-slate-500 -mt-2">
              Just their name and email. They'll get a secure link to complete their own
              information (ownership %, address, SSN, date of birth) and sign — you won't
              fill anything out for them, and they won't see your personal details.
            </p>
            <div className="grid sm:grid-cols-2 gap-3">
              <Field label="First name" req error={errors.first_name} v={draft.first_name} on={(v) => patch('first_name', v)} />
              <Field label="Last name"  req error={errors.last_name}  v={draft.last_name}  on={(v) => patch('last_name', v)} />
              <Field label="Email" req type="email" error={errors.email} v={draft.email} on={(v) => patch('email', v)} />
              <Field label="Mobile (optional — for a text of the link)" type="tel" v={draft.phone} on={(v) => patch('phone', v)} />
            </div>
          </>
        ) : (
          <div className="grid sm:grid-cols-2 gap-3">
            <Field label="First name" req error={errors.first_name} v={draft.first_name} on={(v) => patch('first_name', v)} />
            <Field label="Last name"  req error={errors.last_name}  v={draft.last_name}  on={(v) => patch('last_name', v)} />
            <Field label="Ownership %" req type="number" error={errors.ownership_pct} v={draft.ownership_pct} on={(v) => patch('ownership_pct', v)} />
            <Field label="Date of birth" req type="date" error={errors.dob} v={draft.dob} on={(v) => patch('dob', v)} />
            <Field label="SSN" req type="text" placeholder="XXX-XX-XXXX" error={errors.ssn} v={draft.ssn} on={(v) => patch('ssn', v)} />
            <Field label="Email" type="email" v={draft.email} on={(v) => patch('email', v)} />
            <Field label="Phone" type="tel" v={draft.phone} on={(v) => patch('phone', v)} />
            <div />
            <Field label="Street" req error={errors.address_line1} v={draft.address_line1} on={(v) => patch('address_line1', v)} />
            <Field label="Suite / unit" v={draft.address_line2} on={(v) => patch('address_line2', v)} />
            <Field label="City" req error={errors.city} v={draft.city} on={(v) => patch('city', v)} />
            <Select label="State" req error={errors.state} v={draft.state} on={(v) => patch('state', v)} options={STATES} />
            <Field label="ZIP" req error={errors.zip} v={draft.zip} on={(v) => patch('zip', v)} />
          </div>
        )}
        <div className="flex flex-wrap gap-2 items-center">
          <button className="btn-primary" onClick={commitDraft} disabled={busy}>
            {busy ? 'Saving…' : editingId ? 'Save changes' : owners.length === 0 ? 'Add owner' : 'Add co-owner invite'}
          </button>
          {editingId && (
            <button className="btn-ghost" onClick={() => { setDraft(empty); setEditingId(null); setErrors({}); setGlobalError(null); }}>Cancel</button>
          )}
          <p className="text-xs text-slate-500 ml-auto">
            {owners.length === 0 && !draftFilled && 'Fill in this form, then click Add owner.'}
            {owners.length > 0 && !draftFilled && 'Invite another owner if needed, or click Continue.'}
            {draftFilled && !editingId && 'Click the add button to save, or Continue (we’ll save automatically).'}
          </p>
        </div>
      </div>

      {globalError && <p className="field-error">{globalError}</p>}

      <div className="flex justify-between items-center">
        <button className="btn-ghost" onClick={onBack}>Back</button>
        <button className="btn-primary" onClick={handleContinue} disabled={busy}>
          {busy ? 'Saving…' : 'Continue'}
        </button>
      </div>
    </div>
  );
}

function Field({ label, type = 'text', v, on, req, error, placeholder }) {
  return (
    <label className="block">
      <span className="field-label">{label}{req && <span className="text-red-500"> *</span>}</span>
      <input
        type={type}
        className={`field-input ${error ? 'is-error' : ''}`}
        aria-invalid={!!error}
        value={v ?? ''}
        placeholder={placeholder}
        onChange={(e) => on(e.target.value)}
      />
      {error && <p className="field-error">{error}</p>}
    </label>
  );
}

function Select({ label, v, on, options, req, error }) {
  return (
    <label className="block">
      <span className="field-label">{label}{req && <span className="text-red-500"> *</span>}</span>
      <select
        className={`field-select ${error ? 'is-error' : ''}`}
        aria-invalid={!!error}
        value={v ?? ''}
        onChange={(e) => on(e.target.value)}
      >
        <option value="">— select —</option>
        {options.map((o) => <option key={o} value={o}>{o}</option>)}
      </select>
      {error && <p className="field-error">{error}</p>}
    </label>
  );
}
